import { mat4Determinant3 } from '../math/mat4-determinant3.js';
import { needsOrmComposite, anyPrimitive } from './gltf-parser.js';

const M = "KHR_materials_";
const GS = "KHR_gaussian_splatting";
const _features = [
  // Pre-parse features (buffer-level): order matters — meshopt decompresses
  // bufferViews first, then sparse accessors are materialized (so their base can
  // read decompressed data), then quantization dequantizes the resulting accessors.
  ["EXT_meshopt_compression", () => import('./gltf-feature-meshopt.js')],
  [(j) => !!j.accessors?.some((a) => a.sparse), () => import('./gltf-feature-sparse.js')],
  ["KHR_mesh_quantization", () => import('./gltf-ext-quantization.js')],
  // Pre-mesh features (geometry decompression)
  ["KHR_draco_mesh_compression", () => import('./gltf-feature-draco.js')],
  // Material extensions
  [M + "clearcoat", () => import('./gltf-ext-clearcoat.js')],
  [M + "iridescence", () => import('./gltf-ext-iridescence.js')],
  [M + "emissive_strength", () => import('./gltf-ext-emissive-strength.js')],
  [M + "sheen", () => import('./gltf-ext-sheen.js')],
  [M + "anisotropy", () => import('./gltf-ext-anisotropy.js')],
  [M + "diffuse_transmission", () => import('./gltf-ext-diffuse-transmission.js')],
  [M + "unlit", () => import('./gltf-ext-unlit.js')],
  [M + "pbrSpecularGlossiness", () => import('./gltf-ext-spec-gloss.js')],
  // Dielectric cluster (ior/specular/transmission/volume/dispersion) — any of the five triggers the
  // loader; transmission refraction is wired dynamically by the PBR material path when needed.
  [(j) => ["transmission", "volume", "ior", "specular", "dispersion"].some((e) => j.extensionsUsed?.includes(M + e)), () => import('./gltf-ext-dielectric.js')],
  ["KHR_texture_transform", () => import('./gltf-ext-uv-transform.js')],
  ["KHR_texture_basisu", () => import('./gltf-ext-basisu.js')],
  [needsOrmComposite, () => import('./gltf-ext-orm.js')],
  // Per-mesh features (predicates inlined to avoid eager imports)
  [(j) => !!j.skins?.length && anyPrimitive(j, (p) => p.attributes?.JOINTS_0 !== void 0), () => import('./gltf-feature-skeleton.js')],
  [(j) => anyPrimitive(j, (p) => !!p.targets?.length), () => import('./gltf-feature-morph.js')],
  // Non-triangle primitive topology (POINTS/LINES/LINE_STRIP/TRIANGLE_STRIP) or a
  // negative-determinant node (negative scale / mirrored matrix): both need the lazy primitive
  // feature (topology threading + winding reversal). Triangle-list positive-winding never triggers.
  //
  // Gaussian-splatting primitives are POINTS-mode by definition, but the GS feature consumes them
  // into a GaussianSplattingMesh with its own pipeline, so they never reach the PBR pipeline that
  // reads the primitive state — loading the feature for them fetches a chunk nothing then reads.
  // A GS asset that ALSO carries a genuinely exotic primitive, or a mirrored node, still triggers.
  [(j) => hasNegDetNode(j) || anyPrimitive(j, (p) => p.mode !== void 0 && p.mode !== 4 && !p.extensions?.[GS]), () => import('./gltf-feature-primitive.js')],
  // Per-asset features
  [hasGltfExtras, () => import('./gltf-feature-extras.js')],
  ["KHR_lights_punctual", () => import('./gltf-feature-lights-punctual.js')],
  [GS, () => import('./gltf-feature-gaussian-splatting.js')],
  ["EXT_lights_image_based", () => import('./gltf-ext-lights-image-based.js')],
  [(j) => !!j.animations?.length, () => import('./gltf-feature-animations.js')],
  // Non-Float32 / normalized animation sampler accessors (e.g. Animation_SamplerType normalized
  // BYTE/SHORT rotation) need the lazy denorm converter; plain float samplers never load it.
  [hasNonFloatAnimSampler, () => import('./gltf-sampler-denorm.js')],
  [M + "variants", () => import('./gltf-feature-variants.js')],
  ["KHR_node_visibility", () => import('./gltf-ext-node-visibility.js')],
  ["KHR_animation_pointer", () => import('./gltf-feature-animation-pointer.js')],
  ["EXT_mesh_gpu_instancing", () => import('./gltf-feature-gpu-instancing.js')],
  ["KHR_xmp_json_ld", () => import('./gltf-feature-xmp.js')],
  [(j) => !!(j.extensions?.KHR_interactivity || j.extensions?.BABYLON_flow_graph), () => import('./gltf-feature-interactivity.js')]
];
async function loadGltfFeatures(json) {
  const used = json.extensionsUsed ?? [];
  const mods = await Promise.all(_features.flatMap(([t, load]) => (typeof t === "string" ? used.includes(t) : t(json)) ? [load()] : []));
  return mods.map((m) => m.default);
}
async function runGltfMaterialFeatures(mat, features, ctx) {
  const fragments = await Promise.all(features.map((feature) => feature.applyMaterial(mat, ctx)));
  let layers;
  for (const fragment of fragments) {
    if (fragment) {
      Object.assign(layers ??= {}, fragment);
    }
  }
  return layers;
}
function hasGltfExtras(json) {
  const hasExtras = (item) => item?.extras !== void 0;
  return [json.asset, json.nodes, json.materials, json.animations, json.meshes].flat().some(hasExtras) || anyPrimitive(json, hasExtras);
}
function hasNonFloatAnimSampler(json) {
  const accessors = json.accessors;
  return !!json.animations?.some(
    (a) => a.samplers?.some((s) => accessors[s.input]?.componentType !== 5126 || accessors[s.output]?.componentType !== 5126)
  );
}
function hasNegDetNode(json) {
  return !!json.nodes?.some((n) => {
    if (n.scale) {
      return n.scale[0] * n.scale[1] * n.scale[2] < 0;
    }
    if (n.matrix) {
      return mat4Determinant3(n.matrix) < 0;
    }
    return false;
  });
}

export { loadGltfFeatures, runGltfMaterialFeatures };
//# sourceMappingURL=gltf-feature-registry.js.map
